import {useCallback, useEffect, useMemo, useState} from 'react';
import {useAppDispatch, useAppSelector} from 'store/customer';
import {useJobContext} from 'contexts';
import {RoomPlannerProduct} from 'components/customer/RoomPlanner/types';
import {
    DuplicateIds,
    duplicateIdsSetAll,
    getDuplicateIds,
    setProducts,
} from 'components/customer/RoomPlanner/store/roomPlannerSlice';
import {
    appendDuplicateIdIndex,
    getDuplicateIdList,
    upsertDuplicateIdList,
} from 'components/customer/RoomPlanner/helpers/duplicateIdsHelpers';
import {useGetRoomProductsQuery} from 'components/customer/Product/store/productApi';
import {Product} from 'components/customer/Product/entity/Product';
import {mapTemplate} from 'components/customer/RoomPlanner/helpers/mapTemplate';
import {shallowEqual} from 'react-redux';
import {isEqual} from 'lodash';
import {useGetLayoutsQuery} from 'components/customer/RoomPlanner/store/roomPlannerApi';
import {useGetRoomPreviewQuery} from 'components/customer/Preview3D/store/previewApi';
import {useRoomPreview} from 'components/customer/RoomPlanner/components/helpers/RoomPreview';
import {useParams} from 'react-router-dom';
import {PartialJob} from 'shared/types/PartialJob';

const useRoomProduct = () => {
    const dispatch = useAppDispatch();
    const isRoomPreview = useRoomPreview();
    const jobContext = useJobContext() as PartialJob | undefined;
    const {roomSlug = ''} = useParams();

    const {
        data: roomPreviewData,
        isLoading: isPreviewLoading,
        isUninitialized: isPreviewUninitialized,
    } = useGetRoomPreviewQuery(
        {slug: roomSlug},
        {
            skip: !isRoomPreview || !roomSlug,
        }
    );
    const room = isRoomPreview ? roomPreviewData?.room : jobContext?.room;
    const roomId = isRoomPreview ? roomPreviewData?.room.id : room?.id;
    const [isLoading, setIsLoading] = useState(true);
    const [duplicatesSet, setDuplicatesSet] = useState(false);
    const duplicateIds = useAppSelector(getDuplicateIds, shallowEqual);

    const skipAuthQueries =
        isRoomPreview || String(roomId) === 'undefined' || !roomId;

    const {
        data,
        isLoading: isLayoutLoading,
        isUninitialized: isLayoutUninitialized,
    } = useGetLayoutsQuery(parseInt(String(roomId)), {
        skip: skipAuthQueries,
    });
    const wait = !isRoomPreview && (isLayoutLoading || isLayoutUninitialized);

    const {
        data: {products: roomPlannerData} = {},
        isLoading: isApiLoading,
        isFetching,
        isUninitialized,
    } = useGetRoomProductsQuery(
        {roomId: parseInt(String(roomId))},
        {
            skip: skipAuthQueries || wait,
        }
    );

    const layoutData = useMemo(() => {
        if (isRoomPreview) {
            return roomPreviewData?.layout
                ? {layout: roomPreviewData.layout, success: 1}
                : undefined;
        }
        return data;
    }, [isRoomPreview, roomPreviewData, data]);

    const products = isRoomPreview
        ? roomPreviewData?.job_cabinets
        : roomPlannerData;
    // Unknown until the products request resolves; once it has, only count
    // the products that will actually be drawn in the room.
    const hasProductsToLoad = products
        ? products.some((product) => product.in_qfp_summary != 1)
        : true;

    const roomPlannerProductMapper = useCallback(
        async (productsToMap: Product[]) => {
            const roomPlannerFinalData: RoomPlannerProduct[] = [];
            let allIds: DuplicateIds = {};

            const mappedProducts = await Promise.all(
                productsToMap
                    .filter((product) => product.in_qfp_summary != 1)
                    .map(mapTemplate)
            );

            mappedProducts.forEach((product) => {
                const quantity = product.quantity;
                const newIds: number[] = [];
                const cabinetKey = String(product.job_cabinet_id);
                const ids = getDuplicateIdList(duplicateIds, cabinetKey);

                let id = 0;
                for (let i = 0; i < quantity; i++, id++) {
                    if (ids.length > 0) {
                        if (i < quantity) {
                            if (!ids.includes(id)) {
                                const nextId = ids[Number(i)];
                                id = nextId || id;
                            }
                        }
                    }

                    roomPlannerFinalData.push({
                        ...product,
                        duplicate_id: `${product.job_cabinet_id}-${id}`,
                    });
                    newIds.push(id);
                }

                allIds = upsertDuplicateIdList(allIds, cabinetKey, newIds);
            });

            if (!isEqual(duplicateIds, allIds)) {
                dispatch(duplicateIdsSetAll(allIds));
            }
            dispatch(setProducts(roomPlannerFinalData));
            setIsLoading(false);
        },
        [duplicateIds]
    );

    useEffect(() => {
        if (layoutData?.layout) {
            if (layoutData.layout.layout_room_products) {
                let duplicates: DuplicateIds = {};

                layoutData.layout.layout_room_products.forEach((product) => {
                    const cabinetKey = String(product.job_cabinet_id);
                    duplicates = appendDuplicateIdIndex(
                        duplicates,
                        cabinetKey,
                        product.index ?? 0
                    );
                });

                duplicates = Object.fromEntries(
                    Object.entries(duplicates).map(([key, ids]) => [
                        key,
                        [...ids].sort((a, b) => a - b),
                    ])
                );

                dispatch(duplicateIdsSetAll(duplicates));
                setDuplicatesSet(true);
            }
        }
    }, [layoutData, dispatch]);

    useEffect(() => {
        if (products && duplicatesSet) {
            void roomPlannerProductMapper(products);
        }
    }, [products, duplicatesSet, roomPlannerProductMapper]);

    useEffect(() => {
        return () => {
            dispatch(setProducts([]));
        };
    }, []);

    useEffect(() => {
        if (isRoomPreview) {
            if (isPreviewLoading || isPreviewUninitialized) {
                setIsLoading(true);
            }
            return;
        }

        if (isApiLoading || isFetching || isUninitialized) {
            setIsLoading(true);
        }
    }, [
        isRoomPreview,
        isPreviewLoading,
        isPreviewUninitialized,
        isApiLoading,
        isFetching,
        isUninitialized,
    ]);

    return {
        isLoading,
        hasProductsToLoad,
    };
};

export default useRoomProduct;
